import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import ScrollReveal from '../components/ScrollReveal'
import './Location.css'
import '../styles/shared-hero.css'

interface LocationData {
  city: string
  region?: string
  description: string
  techScene: string
  challenges: string[]
  history: string
}

const locations: Record<string, LocationData> = {
  london: {
    city: 'London',
    description: 'The UK\'s technology capital, where ambitious startups meet enterprise heritage. From fintech in Canary Wharf to AI labs in Kings Cross, London\'s tech ecosystem is unmatched in scale and diversity.',
    techScene: 'London hosts Europe\'s largest concentration of tech firms, with particular strengths in financial technology, artificial intelligence, and SaaS platforms. The city attracts global talent and capital, creating a competitive environment where technical leadership makes the difference between scaling successfully and getting lost in the noise.',
    challenges: [
      'Attracting and retaining engineering talent in a hyper-competitive market',
      'Navigating complex regulatory requirements across finance, data protection, and emerging tech',
      'Scaling infrastructure to handle global traffic while maintaining performance',
      'Integrating legacy systems with modern cloud-native architectures'
    ],
    history: 'From the industrial revolution to the digital age, London has consistently reinvented itself as a centre of innovation. Today\'s Tech City builds on centuries of commercial expertise, combining established financial institutions with disruptive newcomers.'
  },
  cheltenham: {
    city: 'Cheltenham',
    region: 'Gloucestershire',
    description: 'A Regency spa town transformed into a cybersecurity and technology hub. Home to GCHQ and a growing ecosystem of cyber-focused businesses, Cheltenham offers a unique blend of heritage and high-tech.',
    techScene: 'Cheltenham\'s technology sector centres on cybersecurity, digital identity, and government-adjacent technology. The Cyber Park and innovation districts are attracting startups and established firms alike, drawn by the talent pipeline and quality of life.',
    challenges: [
      'Scaling beyond the traditional defence and security client base',
      'Attracting commercial technology talent to a smaller market',
      'Building products suitable for civilian markets while maintaining security expertise',
      'Competing for attention and investment against larger urban centres'
    ],
    history: 'Famous for its cultural festivals and Regency architecture, Cheltenham has quietly become one of the UK\'s most important technology centres. The presence of GCHQ since 1951 created a foundation of technical expertise that now supports a broader commercial ecosystem.'
  },
  bristol: {
    city: 'Bristol',
    description: 'The South West\'s creative and technology powerhouse. With strengths in aerospace, media, and environmental technology, Bristol combines industrial heritage with cutting-edge innovation.',
    techScene: 'Bristol\'s technology sector spans aerospace and engineering simulation, creative industries, cleantech, and semiconductor design. The city\'s universities produce strong technical talent, while the quality of life attracts experienced professionals from London and beyond.',
    challenges: [
      'Diversifying beyond aerospace and defence into commercial software',
      'Scaling engineering teams in a market with strong competition for talent',
      'Modernising legacy industrial systems and processes',
      'Building the infrastructure to support growth-stage companies'
    ],
    history: 'From Brunel\'s engineering marvels to Concorde and beyond, Bristol has always been a city that builds things. Today\'s technology sector continues that tradition, with particular strengths in simulation, robotics, and creative technology.'
  },
  dubai: {
    city: 'Dubai',
    description: 'The Middle East\'s technology gateway, where ambitious visions meet rapid execution. Dubai\'s government-led digital transformation creates unique opportunities for technology leaders.',
    techScene: 'Dubai is positioning itself as a global technology hub, with significant investment in fintech, logistics technology, smart city infrastructure, and blockchain. Free zones like DIFC and DMCC offer favourable conditions for technology businesses serving regional and global markets.',
    challenges: [
      'Navigating regulatory requirements across multiple jurisdictions',
      'Building technology teams in a competitive, transient market',
      'Adapting global products for regional requirements and preferences',
      'Managing rapid organisational growth while maintaining quality'
    ],
    history: 'In just decades, Dubai transformed from a trading port to a global metropolis. The current focus on technology and innovation continues this pattern of rapid, ambitious development, with government initiatives driving adoption across both public and private sectors.'
  },
  'abu-dhabi': {
    city: 'Abu Dhabi',
    description: 'The UAE\'s capital, where long-term strategic investment meets technology transformation. Abu Dhabi offers stability and scale for businesses building for the future.',
    techScene: 'Abu Dhabi\'s technology ecosystem focuses on fintech, renewable energy technology, artificial intelligence, and life sciences. Substantial sovereign investment creates opportunities for companies aligned with national strategic priorities, particularly in sustainability and knowledge-based industries.',
    challenges: [
      'Navigating government procurement and partnership processes',
      'Building sustainable business models beyond initial funding rounds',
      'Attracting and retaining technical talent in a smaller market than Dubai',
      'Translating innovation into commercial products and services'
    ],
    history: 'As the UAE\'s capital and largest emirate by area, Abu Dhabi has traditionally been associated with oil and gas. The ongoing economic diversification places technology at the centre of future growth, with substantial resources committed to developing a knowledge-based economy.'
  }
}

function Location({ locationKey }: { locationKey: string }) {
  const location = locations[locationKey]
  
  if (!location) {
    return null
  }

  const regionText = location.region ? `${location.city}, ${location.region}` : location.city
  const regionCode = locationKey === 'dubai' || locationKey === 'abu-dhabi' ? 'uae' : 'gb'

  return (
    <>
      <SEO 
        title={`Fractional CTO ${location.city}`}
        description={`Fractional CTO services in ${regionText}. Strategic technology leadership for growing companies in the ${location.city} area.`}
        path={`/${regionCode}/${locationKey}`}
      />
      
      {/* Hero Section */}
      <section className="location-hero page-hero">
        <div className="page-hero-content location-hero-content">
          <ScrollReveal>
            <h1>Fractional CTO <em>{location.city}</em></h1>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <p className="location-hero-description">
              Strategic technology leadership for {location.city}'s growing companies
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Description Section */}
      <section className="location-section">
        <div className="section-grid">
          <div className="section-label">
            {location.city}
            <span></span>
          </div>
          <ScrollReveal className="location-content">
            <h2>{location.description}</h2>
          </ScrollReveal>
        </div>
      </section>

      {/* Tech Scene Section */}
      <section className="location-section location-section-alt">
        <div className="section-grid">
          <div className="section-label">
            Tech Scene
            <span></span>
          </div>
          <ScrollReveal className="location-content">
            <p className="location-text">{location.techScene}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="location-section">
        <div className="section-grid">
          <div className="section-label">
            Challenges
            <span></span>
          </div>
          <div className="location-content">
            <ScrollReveal>
              <p className="location-intro">
                Companies in {location.city} face specific technology challenges that benefit from experienced leadership:
              </p>
            </ScrollReveal>
            <ul className="location-challenges">
              {location.challenges.map((challenge, index) => (
                <ScrollReveal key={index} delay={index * 100}>
                  <li>{challenge}</li>
                </ScrollReveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="location-section location-section-alt">
        <div className="section-grid">
          <div className="section-label">
            History
            <span></span>
          </div>
          <ScrollReveal className="location-content">
            <p className="location-text">{location.history}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <section className="location-cta-section">
        <div className="location-cta-content">
          <ScrollReveal>
            <h2>Looking for a fractional CTO in {location.city}?</h2>
            <p>
              We work with companies across {location.city} and beyond. 
              Let's discuss how part-time technology leadership could help your business.
            </p>
            <Link to="/contact" className="cta-button-link">Get in touch</Link>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}

export default Location
